// src/components/ecommerce/PromoSection.jsx
import React, { useState, useEffect } from 'react';
import { 
  Percent,
  Gift,
  Truck,
  Clock,
  Star,
  Zap,
  Tag,
  ArrowRight,
  X,
  ExternalLink,
  ShoppingBag,
  Heart,
  Award,
  Crown,
  Calendar,
  Timer,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

export const PromoSection = ({ 
  promotions = [],
  styling = {},
  onPromoClick,
  layout = 'banner', // banner, cards, ticker, modal
  autoRotate = true,
  showCountdown = true,
  className = ''
}) => {
  const [currentPromo, setCurrentPromo] = useState(0);
  const [timeLeft, setTimeLeft] = useState({});
  const [dismissedPromos, setDismissedPromos] = useState([]);

  const defaultPromotions = [
    {
      id: 'welcome',
      type: 'discount',
      title: 'Welcome Offer',
      description: 'Get 10% off your first order',
      code: 'WELCOME10',
      discount: 10,
      icon: Gift,
      color: 'blue',
      ctaText: 'Shop Now',
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
    },
    {
      id: 'freeship',
      type: 'shipping',
      title: 'Free Shipping',
      description: 'Free delivery on orders over R500',
      icon: Truck,
      color: 'green',
      ctaText: 'Shop Now',
      threshold: 500
    },
    {
      id: 'flash',
      type: 'flash_sale',
      title: 'Flash Sale',
      description: 'Up to 50% off selected items',
      discount: 50,
      icon: Zap,
      color: 'red',
      ctaText: 'Shop Sale',
      expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000) // 2 hours
    }
  ];

  const allPromotions = promotions.length > 0 ? promotions : defaultPromotions;
  const activePromotions = allPromotions.filter(promo => !dismissedPromos.includes(promo.id));

  useEffect(() => {
    if (autoRotate && activePromotions.length > 1) {
      const interval = setInterval(() => {
        setCurrentPromo(prev => (prev + 1) % activePromotions.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activePromotions.length, autoRotate]);

  useEffect(() => {
    if (showCountdown) {
      const interval = setInterval(() => {
        const newTimeLeft = {};
        activePromotions.forEach(promo => {
          if (promo.expiresAt) {
            const now = new Date();
            const expiry = new Date(promo.expiresAt);
            const diff = expiry - now;
            
            if (diff > 0) {
              const days = Math.floor(diff / (1000 * 60 * 60 * 24));
              const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
              const seconds = Math.floor((diff % (1000 * 60)) / 1000);
              
              newTimeLeft[promo.id] = { days, hours, minutes, seconds };
            }
          }
        });
        setTimeLeft(newTimeLeft);
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [activePromotions, showCountdown]);

  const dismissPromo = (promoId) => {
    setDismissedPromos(prev => [...prev, promoId]);
  };

  const getPromoIcon = (promo) => {
    const IconComponent = promo.icon || (
      promo.type === 'discount' ? Percent :
      promo.type === 'shipping' ? Truck :
      promo.type === 'flash_sale' ? Zap :
      promo.type === 'gift' ? Gift :
      Tag
    );
    return IconComponent;
  };

  const getPromoColor = (promo) => {
    const colors = {
      blue: 'bg-blue-500 text-white',
      green: 'bg-green-500 text-white',
      red: 'bg-red-500 text-white',
      yellow: 'bg-yellow-500 text-white',
      purple: 'bg-purple-500 text-white',
      orange: 'bg-orange-500 text-white'
    };
    return colors[promo.color] || colors.blue;
  };

  const formatCountdown = (time) => {
    if (!time) return '';
    
    if (time.days > 0) {
      return `${time.days}d ${time.hours}h ${time.minutes}m`;
    } else if (time.hours > 0) {
      return `${time.hours}h ${time.minutes}m ${time.seconds}s`;
    } else {
      return `${time.minutes}m ${time.seconds}s`;
    }
  };

  if (!activePromotions.length) {
    return null;
  }

  if (layout === 'ticker') {
    return (
      <div className={`bg-black text-white py-2 overflow-hidden ${className}`}>
        <div className="relative">
          <div className="animate-marquee whitespace-nowrap">
            {activePromotions.map((promo, index) => (
              <span key={promo.id} className="inline-flex items-center gap-2 mx-8">
                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                <span className="font-medium">{promo.title}: {promo.description}</span>
                {promo.code && (
                  <span className="bg-white text-black px-2 py-1 rounded text-xs font-bold">
                    {promo.code}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'cards') {
    return (
      <section className={`py-8 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activePromotions.map((promo) => {
              const IconComponent = getPromoIcon(promo);
              const countdown = timeLeft[promo.id];
              
              return (
                <div
                  key={promo.id}
                  className="relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  onClick={() => onPromoClick?.(promo)}
                >
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-600"></div>
                  </div>

                  {/* Content */}
                  <div className="relative p-6">
                    {/* Icon */}
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${getPromoColor(promo)}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>

                    {/* Title and Description */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {promo.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {promo.description}
                    </p>

                    {/* Promo Code */}
                    {promo.code && (
                      <div className="bg-gray-100 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Use code:</span>
                          <span className="font-mono font-bold text-lg text-gray-900">
                            {promo.code}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Countdown */}
                    {countdown && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-red-800">
                          <Timer className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Ends in: {formatCountdown(countdown)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* CTA Button */}
                    <button
                      className="w-full py-3 px-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors group-hover:scale-105 duration-200 flex items-center justify-center gap-2"
                    >
                      {promo.ctaText || 'Shop Now'}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Dismiss Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissPromo(promo.id);
                    }}
                    className="absolute top-3 right-3 p-1 bg-white/80 hover:bg-white rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Default: Banner Layout
  const currentPromoData = activePromotions[currentPromo];
  const IconComponent = getPromoIcon(currentPromoData);
  const countdown = timeLeft[currentPromoData.id];

  return (
    <section 
      className={`relative overflow-hidden ${className}`}
      style={{
        background: `linear-gradient(135deg, ${styling.primaryColor} 0%, ${styling.accentColor} 100%)`
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6 flex-1">
            {/* Icon */}
            <div className="bg-white/20 p-3 rounded-full">
              <IconComponent className="h-8 w-8 text-white" />
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-2">
                {currentPromoData.title}
              </h3>
              <p className="text-white/90 text-lg">
                {currentPromoData.description}
              </p>
              
              {/* Promo Code */}
              {currentPromoData.code && (
                <div className="inline-flex items-center gap-2 mt-3 bg-white/20 rounded-lg px-4 py-2">
                  <span className="text-white/80 text-sm">Code:</span>
                  <span className="font-mono font-bold text-white text-lg">
                    {currentPromoData.code}
                  </span>
                  <button className="ml-2 p-1 hover:bg-white/20 rounded">
                    <Info className="h-4 w-4 text-white/80" />
                  </button>
                </div>
              )}
            </div>

            {/* Countdown */}
            {countdown && (
              <div className="bg-white/20 rounded-xl p-4 text-center min-w-[200px]">
                <div className="text-white/80 text-sm mb-2 flex items-center justify-center gap-1">
                  <Clock className="h-4 w-4" />
                  Offer expires in:
                </div>
                <div className="text-white font-mono text-xl font-bold">
                  {formatCountdown(countdown)}
                </div>
              </div>
            )}

            {/* CTA Button */}
            <button
              onClick={() => onPromoClick?.(currentPromoData)}
              className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 hover:scale-105 shadow-lg flex items-center gap-2"
            >
              {currentPromoData.ctaText || 'Shop Now'}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 ml-6">
            {/* Carousel Dots */}
            {activePromotions.length > 1 && (
              <div className="flex gap-2">
                {activePromotions.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPromo(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === currentPromo ? 'bg-white scale-125' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Dismiss Button */}
            <button
              onClick={() => dismissPromo(currentPromoData.id)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors ml-4"
            >
              <X className="h-5 w-5 text-white/80" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};